import { Table, Tag } from 'components';
import { type VFC, useCallback } from 'react';
import type { SorterResult } from 'antd/lib/table/interface';
import type { SerializedHardwarePackage } from '@enmon/commons/lib/tools';
import { useDeleteHardwarePackage } from 'api/hooks/hardware-package/useDelete';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useHWPackagesFilterWithPagination } from '../hooks/useHWPackagesFilterWithPagination';
import { ActionButton } from './button/Action';
import type { HardwarePackagesData } from '../queries/getHardwarePackagesData';

interface HardwarePackagesTableProps {
  data: HardwarePackagesData['hardwarePackages']['data'];
  isLoading: boolean;
  onSuccess: () => void;
  onEdit: (hardware: SerializedHardwarePackage) => void;
}

export const HardwarePackagesTable: VFC<HardwarePackagesTableProps> = ({ data, isLoading, onSuccess, onEdit }) => {
  const history = useHistory();
  const theme = useTheme();
  const { setFilterState, pagination } = useHWPackagesFilterWithPagination();
  const { mutate: deleteHWPackage, isLoading: isDeleting } = useDeleteHardwarePackage({ onSuccess });

  const handleTableChange = (sorter: SorterResult<SerializedHardwarePackage>) => {
    const sorterField = Array.isArray(sorter.field) ? sorter.field.slice(-1) : sorter.field;

    setFilterState({
      ...(sorter.order &&
        sorterField && {
          sort: `${sorterField as string}:${sorter.order.slice(0, -3)}`,
        }),
    });
  };

  const handleClick = useCallback(
    (hwPackage: SerializedHardwarePackage) => {
      history.push(`${history.location.pathname}/${hwPackage.id}/query-builder`);
    },
    [history],
  );

  const handleDelete = useCallback((id: SerializedHardwarePackage['id']) => deleteHWPackage(id), [deleteHWPackage]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name' satisfies keyof SerializedHardwarePackage,
      key: 'name',
      sorter: true,
    },
    {
      title: 'Priority',
      dataIndex: 'priority' satisfies keyof SerializedHardwarePackage,
      key: 'priority',
      sorter: true,
    },
    {
      title: 'Has HW query',
      key: 'hardware_query',
      render: (_, { query }) => (
        <Tag color={query ? theme.color.status.success : theme.color.status.error}>{query ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      width: 150,
      title: 'Actions',
      key: 'action',
      render: (_, hardwarePackage) => (
        <ActionButton
          isLoading={isLoading || isDeleting}
          onClick={() => handleClick(hardwarePackage)}
          onDelete={() => handleDelete(hardwarePackage.id)}
          onEdit={() => onEdit(hardwarePackage)}
        />
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={isLoading || isDeleting}
      pagination={{
        ...pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Hardware packages`,
      }}
      rowKey={item => item.id}
      scroll={{ x: 'fit-content' }}
      onChange={(_, __, sorter) => handleTableChange(Array.isArray(sorter) ? sorter[0] : sorter)}
    />
  );
};
